import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../services/api";

function Index() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const itemsPerPage = 5;

  const fetchCustomers = async (page = 1) => {
    setLoading(true);
    try {
      const res = await api.get(`customers/?page=${page}&page_size=${itemsPerPage}`);
      console.log("API Response:", res.data);
      
      if (res.data.results !== undefined) {
        setCustomers(res.data.results);
        setTotalPages(Math.ceil(res.data.count / itemsPerPage));
        setTotalCount(res.data.count);
      } else if (res.data.count !== undefined) {
        setCustomers(res.data.data || res.data.items || []);
        setTotalPages(Math.ceil(res.data.count / itemsPerPage));
        setTotalCount(res.data.count);
      } else {
        const allCustomers = res.data;
        const startIndex = (page - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const paginatedCustomers = allCustomers.slice(startIndex, endIndex);
        
        setCustomers(paginatedCustomers);
        setTotalPages(Math.ceil(allCustomers.length / itemsPerPage));
        setTotalCount(allCustomers.length);
      }
    } catch (err) {
      console.error(err);
      setError("Failed to load customers.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this customer?")) return;
    try {
      await api.delete(`customers/${id}/`);
      fetchCustomers(currentPage);
    } catch (err) {
      console.error(err);
      alert("Failed to delete customer.");
    }
  };

  useEffect(() => {
    fetchCustomers(currentPage);
  }, [currentPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo(0, 0);
  };

  const renderPaginationButtons = () => {
    if (totalPages <= 1) return null;

    const buttons = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Previous button
    buttons.push(
      <button
        key="prev"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        ←
      </button>
    );

    // First page
    if (startPage > 1) {
      buttons.push(
        <button
          key={1}
          onClick={() => handlePageChange(1)}
          className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          1
        </button>
      );
      if (startPage > 2) {
        buttons.push(
          <span key="ellipsis1" className="px-2 py-1 text-sm text-gray-500">
            ...
          </span>
        );
      }
    }

    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-3 py-1 text-sm border rounded-lg ${
            currentPage === i
              ? "bg-blue-500 text-white border-blue-500"
              : "border-gray-300 hover:bg-gray-50"
          }`}
        >
          {i}
        </button>
      );
    }

    // Last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        buttons.push(
          <span key="ellipsis2" className="px-2 py-1 text-sm text-gray-500">
            ...
          </span>
        );
      }
      buttons.push(
        <button
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
          className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          {totalPages}
        </button>
      );
    }

    // Next button
    buttons.push(
      <button
        key="next"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        →
      </button>
    );

    return buttons;
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
    </div>
  );
  
  if (error) return (
    <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
      <p className="text-red-600 text-lg">{error}</p>
    </div>
  );

  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalCount);

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Customers</h1>
          <p className="text-gray-600 text-sm mt-1">
            {totalCount > itemsPerPage ? `Showing ${startItem}-${endItem} of ${totalCount} customers` : `Showing ${totalCount} customer${totalCount !== 1 ? 's' : ''}`}
          </p>
        </div>
        <Link
          to="/customers/create"
          className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors duration-200 flex items-center space-x-2 text-sm font-medium"
        >
          <span>+</span>
          <span>Add Customer</span>
        </Link>
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">ID</th>
                <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Name</th>
                <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Email</th>
                <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Phone</th>
                <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Tazkara</th>
                <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Company</th>
                <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Address</th>
                <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {customers.length === 0 ? (
                <tr>
                  <td colSpan="8" className="p-6 text-center">
                    <div className="text-gray-500 text-sm mb-2">No customers found</div>
                    <Link
                      to="/customers/create"
                      className="text-blue-500 hover:text-blue-600 text-sm font-medium"
                    >
                      Create your first customer
                    </Link>
                  </td>
                </tr>
              ) : (
                customers.map(c => (
                  <tr key={c.id} className="hover:bg-gray-50 transition-colors duration-150">
                    <td className="p-3 text-xs font-medium text-gray-900">{c.id}</td>
                    <td className="p-3">
                      <div className="text-sm font-medium text-gray-900">{c.name}</div>
                    </td>
                    <td className="p-3">
                      <div className="text-sm text-gray-600">{c.email || "-"}</div>
                    </td>
                    <td className="p-3">
                      <div className="text-sm text-gray-600">{c.phone || "-"}</div>
                    </td>
                    <td className="p-3">
                      <div className="text-sm text-gray-600">{c.tazkara_number || "-"}</div>
                    </td>
                    <td className="p-3">
                      <div className="text-sm text-gray-600">{c.company || "-"}</div>
                    </td>
                    <td className="p-3">
                      <div className="text-sm text-gray-600 max-w-xs truncate">{c.address || "-"}</div>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center space-x-2">
                        <Link
                          to={`/customers/edit/${c.id}`}
                          className="text-blue-500 hover:text-blue-600 p-1 rounded hover:bg-blue-50 transition-colors duration-200"
                          title="Edit"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                          </svg>
                        </Link>
                        <button
                          onClick={() => handleDelete(c.id)}
                          className="text-red-500 hover:text-red-600 p-1 rounded hover:bg-red-50 transition-colors duration-200"
                          title="Delete"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile Cards - Compact */}
      <div className="md:hidden space-y-3">
        {customers.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 text-center">
            <div className="text-gray-500 text-sm mb-2">No customers found</div>
            <Link
              to="/customers/create"
              className="text-blue-500 hover:text-blue-600 text-sm font-medium"
            >
              Create your first customer
            </Link>
          </div>
        ) : (
          customers.map(c => (
            <div key={c.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
              <div className="flex justify-between items-start mb-2">
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-gray-900 text-sm truncate">{c.name}</h3>
                  <p className="text-xs text-gray-500">ID: {c.id}</p>
                </div>
                <div className="flex items-center space-x-1 ml-2">
                  <Link
                    to={`/customers/edit/${c.id}`}
                    className="text-blue-500 hover:text-blue-600 p-1 rounded hover:bg-blue-50 transition-colors duration-200"
                    title="Edit"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                    </svg>
                  </Link>
                  <button
                    onClick={() => handleDelete(c.id)}
                    className="text-red-500 hover:text-red-600 p-1 rounded hover:bg-red-50 transition-colors duration-200"
                    title="Delete"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                    </svg>
                  </button>
                </div>
              </div>
              
              <div className="space-y-1">
                <div className="flex items-center">
                  <svg className="w-3 h-3 text-gray-400 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                  </svg>
                  <span className="text-xs text-gray-600 truncate">{c.email || "No email"}</span>
                </div>
                
                <div className="flex items-center">
                  <svg className="w-3 h-3 text-gray-400 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                  </svg>
                  <span className="text-xs text-gray-600">{c.phone || "No phone"}</span>
                </div>
                
                {c.tazkara_number && (
                  <div className="flex items-center">
                    <svg className="w-3 h-3 text-gray-400 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                    </svg>
                    <span className="text-xs text-gray-600">Tazkara: {c.tazkara_number}</span>
                  </div>
                )}
                
                {c.company && (
                  <div className="flex items-center">
                    <svg className="w-3 h-3 text-gray-400 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
                    </svg>
                    <span className="text-xs text-gray-600 truncate">{c.company}</span>
                  </div>
                )}

                {c.address && (
                  <div className="flex items-start">
                    <svg className="w-3 h-3 text-gray-400 mr-1 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                    </svg>
                    <span className="text-xs text-gray-600 line-clamp-2">{c.address}</span>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-sm text-gray-600">
            Showing {startItem}-{endItem} of {totalCount} customers
          </div>
          <div className="flex items-center space-x-1 flex-wrap justify-center">
            {renderPaginationButtons()}
          </div>
        </div>
      )}
    </div>
  );
}

export default Index;